/* 
// 🔧 judge_results.js
// ✅ Purpose:
// Admin-facing Judge Results Review page for LGV Legacy
//
// ✅ Features:
// - Uses standard LGV admin page layout
// - Loads active event context
// - Loads judged vote documents
// - Aggregates judged points by entry
// - Displays ranking summary
// - Displays scorecard details
// - Uses existing LGV logging utilities
// - Preserves existing Firestore structure
// - No new collections
// - No Firestore writes
*/

import React, { useEffect, useState } from "react";

import { collection, getDocs } from "firebase/firestore";

import { db } from "../../firebase";

import { useEventContext } from "../../hooks/use_event_context";

import { logger } from "../../utils/logger";

import { getEventVotingType } from "../../utils/event_voting_type_helpers";

import { getVotingTypeLabel } from "../../utils/voting_engine_registry";

import { aggregateJudgedPointsByEntry } from "../../utils/points_results_helpers";

import FullPageSpinner from "../../components/FullPageSpinner";

const JudgeResults = () => {
  //
  // =====================================================
  // Event Context
  // =====================================================
  //

  const { eventId, eventDoc, eventLoading, ready } = useEventContext({
    redirectIfNotFound: true,
  });

  //
  // =====================================================
  // Page State
  // =====================================================
  //

  const [results, setResults] = useState([]);

  const [entries, setEntries] = useState({});

  const [voteDetails, setVoteDetails] = useState([]);

  const [voteCount, setVoteCount] = useState(0);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState(null);

  //
  // =====================================================
  // Event Voting Metadata
  // =====================================================
  //

  const votingType = getEventVotingType(eventDoc);

  const votingTypeLabel = getVotingTypeLabel(votingType);

  //
  // =====================================================
  // Load Judge Results
  // =====================================================
  //

  useEffect(() => {
    if (!ready || eventLoading || !eventId) {
      return;
    }

    const loadResults = async () => {
      try {
        logger.log("🧮 Loading judge results:", {
          eventId,
        });

        const votesSnap = await getDocs(
          collection(db, "events", eventId, "votes"),
        );

        const carsSnap = await getDocs(
          collection(db, "events", eventId, "cars"),
        );

        const entryMap = {};

        carsSnap.docs.forEach((doc) => {
          entryMap[doc.id] = {
            id: doc.id,
            ...doc.data(),
          };
        });

        setEntries(entryMap);

        const votes = votesSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setVoteCount(votes.length);

        setVoteDetails(votes);

        const aggregated = aggregateJudgedPointsByEntry(votes);

        //
        // Rank entries by average score.
        // Highest average score wins.
        //
        aggregated.sort(
          (a, b) =>
            Number(b.averageScoreDisplay || 0) -
            Number(a.averageScoreDisplay || 0),
        );

        logger.log("✅ Judge results aggregated:", {
          count: aggregated.length,
          aggregated,
        });

        logger.log("🎯 Judge Results Demo Ready:", {
          eventId,
          entries: aggregated.length,
          evaluations: votes.length,
        });

        setResults(aggregated);
      } catch (err) {
        logger.error("❌ Judge results loading failed:", {
          value: err,
        });

        setError("Unable to load judge results.");
      } finally {
        setLoading(false);
      }
    };

    loadResults();
  }, [ready, eventLoading, eventId]);

  //
  // =====================================================
  // Loading State
  // =====================================================
  //

  if (!ready || eventLoading || loading) {
    return <FullPageSpinner message="Loading Judge Results..." />;
  }

  if (error) {
    return (
      <section className="container-fluid py-5">
        <div className="container">
          <div className="alert alert-danger">{error}</div>
        </div>
      </section>
    );
  }

  //
  // =====================================================
  // Scorecard Detail Helpers
  // =====================================================
  //

  const getScorecardVotesForEntry = (cid) => {
    return voteDetails.filter((vote) => vote.cid === cid);
  };

  //
  // =====================================================
  // Render
  // =====================================================
  //

  return (
    <section id="top-content" className="container-fluid py-5">
      <div
        id="judge-results-review"
        className="container"
        style={{
          maxWidth: "1400px",
        }}
      >
        <div className="row mb-4">
          <div className="col-12">
            <h1 className="display-5 mb-2">Judge Results Review</h1>

            <p className="lead text-muted mb-0">
              {eventDoc?.name && (
                <>
                  {eventDoc.name}
                  <br />
                </>
              )}
              Review judged scoring results for this event.
            </p>
          </div>
        </div>

        <div className="row g-4 mb-4">
          <div className="col-12 col-md-4">
            <div className="card shadow-sm h-100">
              <div className="card-body">
                <div className="text-muted small">Entries Ranked</div>

                <div className="fs-3 fw-bold">{results.length}</div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-4">
            <div className="card shadow-sm h-100">
              <div className="card-body">
                <div className="text-muted small">Judge Evaluations</div>

                <div className="fs-3 fw-bold">{voteCount}</div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-4">
            <div className="card shadow-sm h-100">
              <div className="card-body">
                <div className="text-muted small">Voting Type</div>

                <div className="fs-5 fw-bold">{votingTypeLabel}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="row g-4 mb-4">
          <div className="col-12 col-md-4">
            <div className="card shadow-sm h-100">
              <div className="card-body">
                <div className="text-muted small">
                  Average Evaluations / Entry
                </div>

                <div className="fs-3 fw-bold">
                  {results.length > 0
                    ? (voteCount / results.length).toFixed(1)
                    : "0.0"}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card shadow-sm">
          <div className="card-header bg-white">
            <strong>Ranked Results</strong>
          </div>

          <div className="card-body">
            {results.length === 0 ? (
              <div className="text-muted">No judge results available.</div>
            ) : (
              results.map((result, index) => (
                <div
                  key={result.cid || index}
                  className="border rounded p-3 mb-3"
                >
                  <h4>
                    {index === 0 ? "🏆 Rank #1" : `Rank #${index + 1}`}
                    {" - "}
                    {entries[result.cid]?.regcnt ||
                      entries[result.cid]?.registrationNumber ||
                      entries[result.cid]?.entryNumber ||
                      ""}{" "}
                    {entries[result.cid]?.entryTitle ||
                      entries[result.cid]?.name ||
                      entries[result.cid]?.title ||
                      ""}
                  </h4>

                  <div className="mb-3 text-muted">
                    {entries[result.cid]?.description || ""}
                  </div>

                  <div>
                    <strong>Entry:</strong>{" "}
                    {entries[result.cid]?.entryTitle ||
                      entries[result.cid]?.name ||
                      entries[result.cid]?.title ||
                      result.cid}
                  </div>

                  <div>
                    {entries[result.cid]?.focusArea && (
                      <>
                        <strong>Focus Area:</strong>{" "}
                        {entries[result.cid].focusArea}
                      </>
                    )}
                  </div>
                  <div>
                    Score Summary: <strong>{result.averageScoreDisplay}</strong>
                    {" / "}
                    {result.maxPoints || 25}
                    {" points"}
                  </div>

                  <div>
                    Judges: <strong>{result.judgeCount}</strong>
                  </div>

                  <div className="mt-3">
                    <button
                      className="btn btn-outline-primary w-100 text-start"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target={`#scorecard-${result.cid}`}
                      aria-expanded="false"
                    >
                      View Scorecard Details
                    </button>

                    <div
                      className="collapse mt-3"
                      id={`scorecard-${result.cid}`}
                    >
                      <strong>Scorecard Details</strong>

                      {getScorecardVotesForEntry(result.cid).map(
                        (vote, voteIndex) => (
                          <div
                            key={vote.id || voteIndex}
                            className="mt-2 border rounded p-3"
                          >
                            <div className="mb-2">
                              <strong>
                                Judge:
                              </strong>{" "}
                              {
                                vote.judgeName ||
                                vote.evaluatorName ||
                                vote.voterName ||
                                vote.name ||
                                `Judge ${voteIndex + 1}`
                              }
                            </div>

                            {vote.scores?.map((score, scoreIndex) => (
                                  <div key={score.factorId || scoreIndex}>
                                <div>
                                  <strong>{score.factorName}</strong>{" "}
                                  {score.pointsAwarded}
                                  {" / "}
                                  {score.maxPoints}
                                </div>

                                {score.checkedHighlights?.map(
                                  (highlight, highlightIndex) => (
                                    <div
                                      key={
                                        highlight.highlightId || highlightIndex
                                      }
                                      className="ms-3 text-muted"
                                    >
                                      ✓ {highlight.highlightName}{" "}
                                      {highlight.pointsAwarded}
                                      {" / "}
                                      {highlight.maxPoints}
                                    </div>
                                  ),
                                )}
                              </div>
                            ))}
                          </div>
                        ),
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default JudgeResults;
